import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { KeyboardArrowRight, MoreVert } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Popper, styled } from '@mui/material';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { ConfirmationDelete, ConfirmationWithRadioWrapper } from 'components/Dialogs';
import { ConfirmationWithRadioProps } from 'components/Dialogs/ConfirmationWithRadio';
import AddMultipleMenuItem from 'components/FeaturedSpreadsheet/components/AddMultipleMenuItem';
import { DialogContext } from 'context';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { kpiDataTypes } from 'pages/Financials/kpi/config/constants';
import { useResponse } from 'services/hooks';
import { capitalizeWords, getObjectValue, getStringValue } from 'utilities';
import { getColumnLetter } from 'utilities/alphabet-utilities';
import getCancelDialogOptions from 'utilities/getCancelDialogOptions';
import FeaturedSpreadsheetContext from '../context/FeaturedSpreadsheetContext';
import { copyToClipboard, getCellRef, getValuesToCopy, hasCellRowSpan } from '../utilities/utilities';

const StyledRowIndicator = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: '12px',
  marginLeft: '-12px',
  backgroundColor: theme.palette.primary.main,
  borderRadius: '3px 0px 0px 3px',
  display: 'flex',
  flexWrap: 'wrap',
  alignContent: 'center',
  zIndex: 1,
}));

const StyledMenuButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  padding: '0px',
}));

const StyledMoreVertIcon = styled(MoreVert)(({ theme }) => ({
  fill: theme.palette.common.white,
  width: 'auto !important',
  fontSize: '0.8rem',
}));

const StyledKeyboardArrowRightIcon = styled(KeyboardArrowRight)(({ theme }) => ({
  fill: theme.palette.common.white,
  width: 'auto !important',
  fontSize: '0.8rem',
}));

const StyledDeleteMenuItem = styled(MenuItem)(({ theme }) => ({
  color: theme.palette.error.main,
}));

interface IRowIndicatorProps {
  tableRef: React.RefObject<HTMLElement | null>;
}

interface IndicatorPosition {
  top: number;
  height: number;
}

const RowIndicator = ({ tableRef }: IRowIndicatorProps) => {
  const [showIndicator, setShowIndicator] = useState(false);
  const [indicatorPosition, setIndicatorPosition] = useState<IndicatorPosition>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [cell, setCell] = useState<ICell>();
  const [multipleValue, setMultipleValue] = useState(0);
  const {
    selectedCell,
    setSelectedCell,
    cells,
    displayRowIndicator,
    allowReorderRows,
    allowCopyRows,
    allowAddMultipleRows,
    addMultipleRows,
    allowAddSingleRow,
    addSingleRow,
    displayLegend,
    activeCell,
    tableData,
    allowDeleteRow,
    deleteRowFn,
    allowChangeDataType,
    changeRowDataTypeFn,
  } = useContext(FeaturedSpreadsheetContext);
  const { infoNotification, errorNotification } = useResponse();
  const dialogContext = useContext(DialogContext);

  const getUpdatedCell = useCallback(
    (cellInfo: any) => {
      if (!cellInfo || !cells) {
        return null;
      }
      const { key } = cellInfo;
      const updatedCell = cells[key];
      return updatedCell || cellInfo;
    },
    [cells]
  );

  useEffect(() => {
    if (!selectedCell || !displayRowIndicator) {
      return;
    }
    const { start, end, cellInfo } = selectedCell;
    const updatedCell = getUpdatedCell(cellInfo);
    if (!updatedCell) {
      return;
    }
    setCell(updatedCell);
    const { j: startColumn, i: startRow } = start;
    const { i: endRow } = end;
    // This condition will prevent to show the column indicator when the user has select more than one row.
    if (startRow === endRow) {
      const columnLetter = getColumnLetter(startColumn);
      const cellKey = cellInfo.key ?? `${columnLetter}${startRow}`;
      const cellRef = getCellRef(tableRef as React.RefObject<HTMLElement>, cellKey);
      const hasRowSpan = hasCellRowSpan(cells, cellKey, cellRef);
      if (!hasRowSpan && cellRef) {
        const extraOffsetTop = isEmpty(activeCell) ? 0 : 25;
        setIndicatorPosition({
          top: cellRef.offsetTop + extraOffsetTop,
          height: cellRef.offsetHeight,
        });
        setShowIndicator(true);
      } else {
        setShowIndicator(false);
      }
    }
  }, [selectedCell, cells, tableRef, displayRowIndicator, displayLegend, activeCell, getUpdatedCell]);

  const onCloseDropdown = useCallback(() => setAnchorEl(null), [setAnchorEl]);

  const handleOnMoreOptionsClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const handleOnCopyClick = useCallback(() => {
    onCloseDropdown();
    if (!selectedCell) {
      return;
    }
    const { rowNumber } = selectedCell;
    if (rowNumber) {
      const valuesToCopy = getValuesToCopy(cells, rowNumber, false);
      copyToClipboard(valuesToCopy, infoNotification, errorNotification);
    }
  }, [cells, selectedCell, infoNotification, errorNotification, onCloseDropdown]);

  const handleOnAddSingleRowClick = useCallback(() => {
    onCloseDropdown();
    if (addSingleRow) {
      addSingleRow();
    }
  }, [addSingleRow, onCloseDropdown]);

  const handleOnAddMultipleClick = () => {
    onCloseDropdown();
    if (addMultipleRows) {
      addMultipleRows(multipleValue);
    }
  };

  const removeIndicator = useCallback(() => {
    setSelectedCell(undefined);
    setShowIndicator(false);
  }, [setSelectedCell]);

  const deleteRowCb = useCallback(
    (company: any) => {
      if (deleteRowFn) {
        deleteRowFn(company);
      }
      removeIndicator();
    },
    [deleteRowFn, removeIndicator]
  );

  const showDeleteDialog = useCallback(
    (company: any, preferredMessage: string | null = null) => {
      const companyName = getStringValue(company.target_name ?? company.name);
      const CUSTOM_MESSAGE = `Are you sure you want to delete ${
        company.target_name === '' ? 'this' : ''
      } ${companyName}?`;

      const { showDialog } = dialogContext;

      const ContentComponent = <ConfirmationDelete customMessage={preferredMessage ?? CUSTOM_MESSAGE} />;
      showDialog(
        getCancelDialogOptions(
          () => ContentComponent,
          () => deleteRowCb(company)
        )
      );
    },
    [dialogContext, deleteRowCb]
  );

  const onDeleteTransactionCompany = useCallback(() => {
    if (!tableData?.approach) {
      return;
    }
    const {
      approach: { valuations_approach_gpt: valuationsApproachGpt },
    } = tableData;
    if (valuationsApproachGpt && cell) {
      const { gpt_transactions: gptTransactions } = valuationsApproachGpt;
      if (gptTransactions) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const transaction = gptTransactions.find(item => item.row_ref === cell.alias || item.row_ref === cell.row_ref);
        if (transaction) {
          showDeleteDialog(transaction);
        }
      }
    }
  }, [cell, tableData, showDeleteDialog]);

  // todo: this logic is too specific too the component, we should refactor
  const onDeleteComparisonCompany = useCallback(() => {
    if (tableData?.approach) {
      const {
        approach: { valuations_approach_gpc: valuationsApproachGpc },
      } = tableData;
      if (valuationsApproachGpc && cell) {
        const { gpc_comparison: gpcComparison } = valuationsApproachGpc;
        if (gpcComparison) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const comparison = gpcComparison.find(({ cap_iq_id: capIdId }) =>
            [cell.alias, cell.titleAlias].includes(capIdId)
          );
          if (comparison) {
            showDeleteDialog(comparison);
          }
        }
      }
    }
  }, [cell, tableData, showDeleteDialog]);

  // todo: logic should not be specific to the component, we should refactor
  const onDeleteKpi = useCallback(() => {
    if (tableData?.companyKPIs === undefined) {
      return;
    }
    const { companyKPIs } = tableData;
    if (companyKPIs?.length && cell) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const kpi = companyKPIs.find(({ alias }) => cell.alias === alias);
      if (kpi) {
        showDeleteDialog(
          kpi,
          'Are you sure to want delete this KPI? Deleting it will affect all the measurement dates for this company.'
        );
      }
    }
  }, [cell, tableData, showDeleteDialog]);

  const onDeleteRow = useCallback(() => {
    const { data: cellData } = getObjectValue(cell);

    if (cell && !isEmpty(cellData)) {
      showDeleteDialog(cellData);
    }
  }, [cell, showDeleteDialog]);

  //  todo: this logic is too specific to the component, we should refactor
  const handleOnDeleteRowClick = useCallback(() => {
    onCloseDropdown();
    if (!cell) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (cell.isGptRow) {
      onDeleteTransactionCompany();

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
    } else if (cell.isGpcRow) {
      onDeleteComparisonCompany();

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
    } else if (cell.isKpiRow) {
      onDeleteKpi();
    } else {
      onDeleteRow();
    }
  }, [onCloseDropdown, cell, onDeleteTransactionCompany, onDeleteComparisonCompany, onDeleteKpi, onDeleteRow]);

  // todo: this logic is too specific to the component, we should refactor
  const handleChangeDataType = useCallback(() => {
    if (!cell) {
      return;
    }
    const { showDialog, wrapperProps } = dialogContext;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const currentDataType = cell.sheet?.tableData?.companyKPIs?.find(({ alias }) => alias === cell.alias)?.data_type;
    const dataTypeDialogProps: ConfirmationWithRadioProps = {
      confirmationBaseFn: changeRowDataTypeFn,
      options: kpiDataTypes.map(([value, label]) => ({ value, label: capitalizeWords(label) })),
      cell,
      dialogTitle: 'KPI data types',
      defaultValue: currentDataType,
      open: true,
      onClose: wrapperProps.onClose,
      size: 'xs',
    };
    const wrapper = () => <ConfirmationWithRadioWrapper {...dataTypeDialogProps} />;
    showDialog({ wrapper });
    onCloseDropdown();
  }, [cell, changeRowDataTypeFn, dialogContext, onCloseDropdown]);

  // todo: this logic is too specific to the component, we should refactor
  const allowDelete = useMemo(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    () => allowDeleteRow && deleteRowFn && (cell?.isGptRow || cell?.isGpcRow || cell?.isKpiRow || cell?.isDeletableRow),
    [allowDeleteRow, deleteRowFn, cell]
  );

  // With this function we will determine if do we have to show or not the dropdown menu on the row indicator
  const showMenuOptions = useMemo(() => allowCopyRows || allowDelete, [allowCopyRows, allowDelete]);

  if (!showIndicator) {
    return null;
  }

  return (
    <StyledRowIndicator
      id={`row-indicator-${cell?.key}`}
      style={{ top: indicatorPosition?.top, height: indicatorPosition?.height }}>
      {showIndicator && allowReorderRows && (
        <StyledMenuButton>
          <StyledMoreVertIcon />
        </StyledMenuButton>
      )}
      {showIndicator && showMenuOptions && (
        <StyledMenuButton onClick={handleOnMoreOptionsClick}>
          <StyledKeyboardArrowRightIcon />
        </StyledMenuButton>
      )}
      {showMenuOptions && (
        <Popper open={open} anchorEl={anchorEl} id={open ? 'transitions-popper' : undefined}>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={onCloseDropdown}>
            {allowCopyRows && <MenuItem onClick={handleOnCopyClick}>COPY</MenuItem>}
            {allowAddSingleRow && addSingleRow && <MenuItem onClick={handleOnAddSingleRowClick}>ADD NEW</MenuItem>}
            {allowAddMultipleRows && addMultipleRows && (
              <AddMultipleMenuItem
                onAddMultiple={handleOnAddMultipleClick}
                multipleValue={multipleValue}
                setMultipleValue={setMultipleValue}
              />
            )}
            {allowChangeDataType && <MenuItem onClick={handleChangeDataType}>CHANGE DATA TYPE</MenuItem>}
            {allowDelete && <StyledDeleteMenuItem onClick={handleOnDeleteRowClick}>DELETE</StyledDeleteMenuItem>}
          </Menu>
        </Popper>
      )}
    </StyledRowIndicator>
  );
};

RowIndicator.propTypes = {
  tableRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

export default RowIndicator;
